import {
    Add,
    AddCircleOutline,
    Remove,
    RemoveCircleOutline,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const clampInt = (value, min = 1, max = Infinity) => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return min;
    return Math.min(Math.max(n, min), max);
};

const SplitItemDialog = ({ open, item, onClose, onConfirm }) => {
    const { t } = useTranslation();

    const totalQty = Math.max(1, Number(item?.qty) || 0);

    // Default split: original qty + one extra row of 0 so users can adjust.
    // Each entry has its own stable id so React inputs don't lose focus on
    // re-render and remove/add stays predictable.
    const [rows, setRows] = useState([]);
    const [rowIdSeq, setRowIdSeq] = useState(0);

    useEffect(() => {
        if (!open || !item) return;
        // Default split puts everything-but-one in the first entry and 1 in
        // the second. This matches the common "I want to peel one off" flow
        // (e.g. one customer paying for just 1 of 6) and only needs one tap
        // to be useful.
        const first = Math.max(1, totalQty - 1);
        const second = Math.max(1, totalQty - first);
        setRows([
            { id: 0, qty: first },
            { id: 1, qty: second },
        ]);
        setRowIdSeq(2);
    }, [open, item, totalQty]);

    const sum = useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.qty) || 0), 0),
        [rows]
    );
    const remaining = totalQty - sum;
    const canConfirm = rows.length >= 2 && sum === totalQty && rows.every((r) => Number(r.qty) > 0);

    const updateRowQty = (id, value) => {
        // Allow empty string while typing; coerce on confirm. Max per-row is
        // bounded by the original total qty (no single split larger than the
        // source).
        const next = value === "" ? "" : clampInt(value, 1, totalQty);
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, qty: next } : r)));
    };

    const stepRowQty = (id, delta) => {
        setRows((prev) => {
            const currentSum = prev.reduce(
                (acc, r) => acc + (Number(r.qty) || 0),
                0
            );
            return prev.map((r) => {
                if (r.id !== id) return r;
                const current = Number(r.qty) || 0;
                const target = current + delta;
                // Floor at 1 so a row never disappears via the stepper
                // (use the remove button to drop a row entirely). Cap at
                // total qty for symmetry with the input clamp.
                if (target < 1 || target > totalQty) return r;
                // Block any increment that would push the sum over the
                // source qty (i.e. would make `remaining` negative).
                if (delta > 0 && currentSum + delta > totalQty) return r;
                return { ...r, qty: target };
            });
        });
    };

    const addRow = () => {
        if (remaining <= 0) return;
        setRows((prev) => [
            ...prev,
            { id: rowIdSeq, qty: Math.max(1, remaining) },
        ]);
        setRowIdSeq((n) => n + 1);
    };

    const removeRow = (id) => {
        if (rows.length <= 2) return;
        setRows((prev) => prev.filter((r) => r.id !== id));
    };

    const handleConfirm = () => {
        if (!canConfirm) return;
        onConfirm?.(rows.map((r) => Number(r.qty)));
    };

    if (!item) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {t("pos.splitItemTitle", {
                    name: item?.item?.item_name || item?.item?.item_code || "",
                })}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {t("pos.splitItemDescription", { qty: totalQty })}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    {rows.map((row, index) => {
                        const currentQty = Number(row.qty) || 0;
                        return (
                            <Box
                                key={row.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        minWidth: 60,
                                        color: "text.secondary",
                                    }}
                                >
                                    {t("pos.splitEntry", { n: index + 1 })}
                                </Typography>

                                {/* Stepper: − [input] +  — lets the user
                                  * tap to nudge qty without typing, while
                                  * keeping the field editable for fast jumps. */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => stepRowQty(row.id, -1)}
                                        disabled={currentQty <= 1}
                                        sx={{ borderRadius: 0 }}
                                        aria-label="decrease"
                                    >
                                        <Remove fontSize="small" />
                                    </IconButton>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={row.qty}
                                        onChange={(e) =>
                                            updateRowQty(row.id, e.target.value)
                                        }
                                        inputProps={{
                                            min: 1,
                                            max: totalQty,
                                            style: {
                                                textAlign: "center",
                                                padding: "6px 4px",
                                                MozAppearance: "textfield",
                                            },
                                        }}
                                        variant="standard"
                                        sx={{
                                            width: 56,
                                            "& .MuiInput-underline:before, & .MuiInput-underline:after":
                                                { display: "none" },
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                                {
                                                    WebkitAppearance: "none",
                                                    margin: 0,
                                                },
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => stepRowQty(row.id, 1)}
                                        disabled={
                                            currentQty >= totalQty ||
                                            remaining <= 0
                                        }
                                        sx={{ borderRadius: 0 }}
                                        aria-label="increase"
                                    >
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Box>

                                <IconButton
                                    size="small"
                                    onClick={() => removeRow(row.id)}
                                    disabled={rows.length <= 2}
                                    aria-label={t("pos.splitRemoveRow")}
                                >
                                    <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                    <Button
                        size="small"
                        startIcon={<AddCircleOutline />}
                        onClick={addRow}
                        disabled={remaining <= 0}
                    >
                        {t("pos.splitAddRow")}
                    </Button>
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: 500,
                        color: remaining === 0 ? "success.main" : "error.main",
                    }}
                >
                    <span>{t("pos.splitRemaining")}</span>
                    <span>{remaining}</span>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("common.cancel")}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!canConfirm}
                >
                    {t("pos.splitConfirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SplitItemDialog;
